import './headerPair.scss';

import cn from 'classnames';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, NavLink } from 'react-router-dom';

import { useIsDark } from '@/shared/theme';

import { ApiPairs, ApiProjects } from 'api';
import { UDPairModal } from 'modals';
import ProjectListIconPairs from 'shared/components/PlatformIcon';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch, useTypedSelector } from 'store';
import {
  setPairError,
  setPairLoading,
  setSelectedCexPair,
  setSelectedDexPair,
  setSelectedSolPair,
} from 'store/slices/pairs';
import { IProject } from 'types/project';
import { Tooltip } from 'ui/Tooltip';
import { geckoterminalLink } from 'utils';
import { calculateScanLink } from 'utils/calculates';
import { getPairLinkedBots } from 'utils/getPairLinkedBots';
import { EExchange, web3Configs } from 'web3';

import { PairHeaderSkeleton } from './PairHeaderSkeleton';

import { CogIcon } from 'assets/icons';
import geckoterminalLogo from 'assets/images/geckoterminal_logo.png';

interface IDexPairHeaderProps {
  pairId: number;
  projectId: number;
}

const capitalizeWord = (value: string) => (value ? value[0].toUpperCase() + value.slice(1) : '');

const formatExchangeName = (value: string) => {
  const [rawName, chain] = value.split(':');

  const name = rawName
    .split('_')
    .map(part => (/^v\d+$/i.test(part) ? part.toUpperCase() : capitalizeWord(part)))
    .join(' ');

  return chain ? `${name} (${chain.toUpperCase()})` : name;
};

const DexPairHeader: React.FC<IDexPairHeaderProps> = ({ pairId, projectId }) => {
  const dispatch = useTypedDispatch();
  const isDark = useIsDark();

  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair);
  const projects = useTypedSelector(store => store.projects.projects);
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  const [project, setProject] = useState<IProject | null>(null);

  useEffect(() => {
    const setup = async () => {
      if (projects) {
        try {
          dispatch(setSelectedCexPair(null));
          dispatch(setSelectedDexPair(null));
          dispatch(setSelectedSolPair(null));
          dispatch(setPairLoading(true));
          dispatch(setPairError(false));

          const [activeProject, activePair] = await Promise.all([
            ApiProjects.getProjectById(projectId),
            ApiPairs.getDexPairById(pairId),
          ]);

          if (activeProject.isSuccess && activePair.isSuccess) {
            setProject({
              created_at: activeProject.data.created_at,
              id: activeProject.data.id,
              name: activeProject.data.name,
              cexPairs: [],
              dexPairs: [],
              solPairs: [],
              updated_at: '',
              notes: activeProject.data.notes,
            });

            if ('dex' in activePair.data) {
              dispatch(setSelectedDexPair(activePair.data));
            }

            await getPairLinkedBots({
              pairId: activePair.data.id,
              dispatch,
              exchange: EExchange.dex,
            });
          } else {
            dispatch(setPairError(true));
          }
        } catch (error) {
          console.log(error);
        } finally {
          dispatch(setPairLoading(false));
        }
      }
    };

    setup();
  }, [dispatch, projects, pairId, projectId]);

  const [updateModalOpened, setUpdateModalOpened] = useState<boolean>(false);

  const onOpenUpdateModalOpened = useCallback(() => {
    setUpdateModalOpened(true);
  }, []);

  const onCloseUpdateModalOpened = useCallback(() => {
    setUpdateModalOpened(false);
  }, []);

  const primaryScanAddress = useMemo(() => {
    if (!dexPair) return undefined;

    if (dexPair.dex === 'pancakeswap_inf_cl:bsc') {
      return dexPair.extra?.pool_manager ?? dexPair.address ?? dexPair.pool_id;
    }

    return dexPair.address ?? dexPair.pool_id;
  }, [dexPair]);

  const secondaryScanAddress = useMemo(() => {
    if (!dexPair?.extra?.pool_manager) return undefined;
    if (
      dexPair.dex === 'pancakeswap_inf_cl:bsc' &&
      dexPair.extra.pool_manager === primaryScanAddress
    ) {
      return undefined;
    }

    return dexPair.extra.pool_manager;
  }, [dexPair, primaryScanAddress]);

  const geckoterminalLinkUrl = useMemo(() => {
    if (!dexPair) return undefined;
    if (dexPair.dex === 'pancakeswap_inf_cl:bsc') return undefined;

    const poolAddress = dexPair.address ?? dexPair.pool_id;

    return poolAddress
      ? geckoterminalLink({ network: dexPair.network, pairAddress: poolAddress })
      : undefined;
  }, [dexPair]);

  if (!project || !dexPair) {
    return <PairHeaderSkeleton />;
  }

  return (
    <div className="mm-header__header-pair">
      {project && dexPair && (
        <>
          <div className="mm-header__header-pair__info">
            <div className="pair-name">
              <motion.a
                href={calculateScanLink({
                  address: dexPair.token_base.address,
                  network: dexPair.network,
                  addressType: 'token',
                })}
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.1 }}
              >
                {dexPair.token_base.symbol}
              </motion.a>
              <span>/</span>
              <motion.a
                href={calculateScanLink({
                  address: dexPair.token_quote.address,
                  network: dexPair.network,
                  addressType: 'token',
                })}
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.1 }}
              >
                {dexPair.token_quote.symbol}
              </motion.a>
            </div>
            {isAdmin && (
              <motion.div
                whileHover={{ rotate: 180, scale: 1.3 }}
                transition={{ duration: 0.3 }}
                className="pair-settings"
                onClick={onOpenUpdateModalOpened}
              >
                <CogIcon />
              </motion.div>
            )}
            <NavLink
              to={generatePath(ROUTE_PATHS.project, { id: project.id.toString() })}
              className="project-name"
            >
              {project.name}
            </NavLink>
            <Tooltip
              trigger={
                <div
                  className={cn(
                    'header-trading-pair-logo-header',
                    dexPair.dex.includes('uniswap') ? 'uniswap' : dexPair.dex,
                    { 'bg-white': isDark, 'bg-bg-primary': !isDark },
                  )}
                >
                  <ProjectListIconPairs platform={dexPair.dex} className="exchange-icon" />
                </div>
              }
              content={formatExchangeName(dexPair.dex)}
              options={{ placement: 'top', offset: 8 }}
            />
          </div>
          <div className="mm-header__header-pair__bottom">
            {primaryScanAddress && (
              <a
                className={cn('scan-link rounded-full p-[2px]', { 'bg-white': isDark })}
                target="_blank"
                rel="noreferrer"
                href={calculateScanLink({
                  address: primaryScanAddress,
                  addressType: 'address',
                  network: dexPair.network,
                })}
              >
                <img src={web3Configs[dexPair.network].scanLogo} alt="" />
              </a>
            )}
            {secondaryScanAddress && (
              <a
                className={cn('scan-link rounded-full p-[2px]', { 'bg-white': isDark })}
                target="_blank"
                rel="noreferrer"
                href={calculateScanLink({
                  address: secondaryScanAddress,
                  addressType: 'address',
                  network: dexPair.network,
                })}
              >
                <img src={web3Configs[dexPair.network].scanLogo} alt="" />
              </a>
            )}
            {geckoterminalLinkUrl && (
              <a
                className="trade-pair-link"
                target="_blank"
                rel="noreferrer"
                href={geckoterminalLinkUrl}
              >
                <img src={geckoterminalLogo} alt="geckoterminal" />
              </a>
            )}
            {dexPair.notes && isAdmin && <span>{dexPair.notes}</span>}
          </div>
          {updateModalOpened && (
            <UDPairModal
              exchange={EExchange.dex}
              onClose={onCloseUpdateModalOpened}
              onOpen={onOpenUpdateModalOpened}
              pairId={dexPair.id}
              projectId={project.id}
              pairSymbol={dexPair.symbol}
              initialNotes={dexPair.notes ?? ''}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DexPairHeader;
